import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { groupDigits, ungroupDigits } from '@/lib/money';
import type { ApprovalBox } from '@/pages/approval/paperForm';
import { useMarket } from '@/pages/home/useMarket';
import {
  BUDGET_CURRENCIES,
  BUDGET_SECTIONS,
  budgetApprovalCells,
  budgetCellKey,
  budgetSheetTitle,
  budgetTotals,
  emptyBudgetLine,
  enteredTotals,
  formatKrw,
  formatMoney,
  lineEntered,
  isGroupLabel,
  PERIOD_WEEKS,
  periodMonth,
  periodMonthOptions,
  ratesFromMarket,
  switchCurrency,
  splitPeriod,
  usesLocal,
  type BudgetLine,
  type BudgetSectionKey,
  type BudgetSignCell,
  type OverseasBudget,
} from '@/pages/approval/overseasBudget';

/**
 * 해외 경비 예산 신청서 — <b>엑셀 「자금청구_본사양식」 그대로</b> (사용자 지시 2026-11-03 「양식 그대로 가줘」).
 *
 * <pre>
 *            OOO현장 26년 8월 1주차 운전자금 예산 신청서 (직영 및 경비성 항목)
 *   ┌현장 결재──────┐                              ┌본사 결재──────────────────┐
 *   │결│ 담당 │ 소장 │                              │결│ 담당 │ 팀장 │ 임원 │ 대표이사│
 *   └─┴────┴────┘                              └─┴────┴────┴────┴──────┘
 *                                                                   [단위: 고른 통화]
 *   구분 │ 공종명 │ 금회 예정 (2026년 8월) │ 내용 │ 비 고 (품의 유/무)      ← 회색
 *   1. 외주기성      │ 신청 │                                         ← 신청 칸 하늘색
 *   [직영성]  (진회색)                                                 ← 갈래 이름 줄
 *            │ …    │ 64,000.00 │ …
 *   외주기성 소 계 (회색)
 *   …
 *   합 계 (노랑)
 * </pre>
 *
 * <p>열 폭은 엑셀 열 너비(A 15.6 · B 35.9 · C 31.3 · D 76.9 · E 25.8)의 비율이다. 현지 통화 · 환율은 종이에 없다 —
 * 종이 밖 {@link OverseasBudgetControls} 에서 정하고, 적는 동안만 금액 칸 아래에 USD 환산이 작게 보인다.
 */

const LINE = '#000';
const HEAD_BG = '#d9d9d9';
const LABEL_BG = '#a6a6a6';
const BLUE_BG = '#deebf7';
const TOTAL_BG = '#ffff00';

const cell: React.CSSProperties = {
  border: `1px solid ${LINE}`,
  padding: '3px 6px',
  verticalAlign: 'middle',
  fontSize: '11.5px',
  height: '22px',
  wordBreak: 'keep-all',
  overflowWrap: 'break-word',
};
const head: React.CSSProperties = { ...cell, textAlign: 'center', fontWeight: 700, background: HEAD_BG, fontSize: '12px', height: '40px' };
const num: React.CSSProperties = { ...cell, textAlign: 'right', whiteSpace: 'nowrap' };
const box: React.CSSProperties = { width: '100%', minWidth: 0, fontSize: '11px', padding: '2px 4px' };

/** 엑셀 열 너비 비율(%). 적는 표는 끝에 [+ · 삭제] 열이 붙고 그 몫은 내용 칸에서 뺀다. */
function columns(edit: boolean): number[] {
  return edit ? [8.4, 19.4, 16.9, 34.5, 13.8, 7] : [8.4, 19.4, 16.9, 41.5, 13.8];
}

/** 시트 제목 — 엑셀 1행(가운데 · 굵게 · 아래 선). */
export function BudgetSheetTitle({ budget }: { budget: OverseasBudget }) {
  /*
    <b>언제나 한 줄</b>이다(사용자 지시 2026-11-03 「괄호가 줄바뀜이 돼서 깨진다」). 줄을 접지 않고, 폭을 넘으면 글자를
    줄여 넣는다 — 잰 크기가 요소에 박혀 인쇄 창으로 그대로 따라간다(인쇄 종이 폭이 화면 종이 폭과 같다).
  */
  const title = budgetSheetTitle(budget);
  const boxRef = useRef<HTMLParagraphElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [size, setSize] = useState(TITLE_PX);
  useLayoutEffect(() => {
    const boxEl = boxRef.current;
    const textEl = textRef.current;
    if (!boxEl || !textEl) return;
    textEl.style.fontSize = `${TITLE_PX}px`;
    const room = boxEl.clientWidth;
    const need = textEl.scrollWidth;
    setSize(room > 0 && need > room ? Math.max(10, Math.floor(TITLE_PX * (room / need) * 10) / 10) : TITLE_PX);
  }, [title]);
  return (
    <p ref={boxRef} style={{ margin: 0, padding: '0 0 6px', textAlign: 'center', fontWeight: 800, lineHeight: 1.4, borderBottom: `1px solid ${LINE}`, whiteSpace: 'nowrap', overflow: 'hidden' }}>
      <span ref={textRef} style={{ display: 'inline-block', fontSize: `${size}px` }}>{title}</span>
    </p>
  );
}

/** 시트 제목의 기본 글자 크기(px). 길면 이보다 줄인다. */
const TITLE_PX = 19;

function SignBox({ title, cells, width }: { title: string; cells: BudgetSignCell[]; width: number }) {
  const c: React.CSSProperties = { border: `1px solid ${LINE}`, textAlign: 'center', fontSize: '11px', padding: '1px 2px' };
  return (
    <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: `${width}px`, background: 'white' }}>
      <colgroup>
        <col style={{ width: '34px' }} />
        {cells.map((_, i) => <col key={i} />)}
      </colgroup>
      <tbody>
        <tr><th colSpan={cells.length + 1} style={{ ...c, fontSize: '12px', fontWeight: 800 }}>{title}</th></tr>
        <tr>
          <td rowSpan={2} style={{ ...c, fontWeight: 700, lineHeight: 2.6 }}>결<br />재</td>
          {cells.map((x, i) => <td key={i} style={{ ...c, fontWeight: 700, letterSpacing: '2px' }}>{x.label}</td>)}
        </tr>
        <tr>
          {cells.map((x, i) => (
            <td key={i} style={{ ...c, height: '46px', verticalAlign: 'middle' }}>
              {x.signImage ? (
                <img src={x.signImage} alt="" style={{ maxHeight: '40px', maxWidth: '100%' }} />
              ) : (
                <>
                  {x.name && <div style={{ fontWeight: 700, lineHeight: 1.3 }}>{x.name}</div>}
                  {(x.stamp || x.date) && (
                    <div style={{ fontSize: '9.5px', color: '#1d4ed8', lineHeight: 1.3 }}>{x.stamp ?? ''} {x.date}</div>
                  )}
                </>
              )}
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  );
}

/** 결재칸 두 개 — 엑셀 2~4행의 그림 자리(왼쪽 현장 결재 · 오른쪽 본사 결재). */
export function BudgetApprovalBoxes({ boxes }: { boxes: ApprovalBox[] }) {
  const cells = budgetApprovalCells(boxes);
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', margin: '12px 0 6px' }}>
      <SignBox title="현장 결재" cells={cells.site} width={190} />
      <SignBox title="본사 결재" cells={cells.hq} width={330} />
    </div>
  );
}

function SheetHead({ budget, edit }: { budget: OverseasBudget; edit: boolean }) {
  return (
    <thead>
      <tr>
        <th style={head}>구분</th>
        <th style={head}>공종명</th>
        <th style={head}>금회 예정<br />({periodMonth(budget.period) || '　'})</th>
        <th style={head}>내용</th>
        <th style={head}>비 고<br />(품의 유/무)</th>
        {edit && <th style={{ ...head, background: 'white', border: 'none' }} />}
      </tr>
    </thead>
  );
}

function SumRow({ label, value, bg, edit, content, note }: {
  label: string; value: string; bg: string; edit: boolean; content: React.ReactNode; note: React.ReactNode;
}) {
  const s: React.CSSProperties = { ...cell, background: bg, fontWeight: 700, fontSize: '12px' };
  return (
    <tr>
      <td style={{ ...s, textAlign: 'center' }} colSpan={2}>{label}</td>
      <td style={{ ...s, textAlign: 'right', whiteSpace: 'nowrap' }}>{value}</td>
      <td style={{ ...s, fontWeight: 400 }}>{content}</td>
      <td style={{ ...s, fontWeight: 400, textAlign: 'center' }}>{note}</td>
      {edit && <td style={{ ...cell, border: 'none' }} />}
    </tr>
  );
}

/** 표 몸통 — 보는 표와 적는 표가 같은 줄 구성을 쓴다. 적는 표만 칸이 입력이고 끝에 [+ · 삭제] 가 붙는다. */
function SheetBody({ budget, onChange }: { budget: OverseasBudget; onChange?: (next: OverseasBudget) => void }) {
  const edit = !!onChange;
  const local = usesLocal(budget);
  const entered = enteredTotals(budget);

  const patchLine = (index: number, part: Partial<BudgetLine>) =>
    onChange?.({ ...budget, lines: budget.lines.map((row, i) => (i === index ? { ...row, ...part } : row)) });
  const removeLine = (index: number) => onChange?.({ ...budget, lines: budget.lines.filter((_, i) => i !== index) });
  /** 그 줄 바로 아래에 빈 줄 — 갈래 이름 아래에 줄을 더 받는 자리(엑셀의 「[셀 빈칸 삽입]」). */
  const insertAfter = (index: number, section: BudgetSectionKey) =>
    onChange?.({ ...budget, lines: [...budget.lines.slice(0, index + 1), emptyBudgetLine(section), ...budget.lines.slice(index + 1)] });
  const appendTo = (section: BudgetSectionKey) => {
    const last = budget.lines.map((row) => row.section).lastIndexOf(section);
    insertAfter(last < 0 ? budget.lines.length - 1 : last, section);
  };

  /** 줄 밖의 적는 칸(갈래 제목 줄 · 소계 · 합계). 적는 표에서는 입력, 보는 표에서는 글자. */
  const free = (key: string, align: 'left' | 'center' | 'right' = 'left', fallback = '') => {
    const value = budget.cells?.[key] ?? fallback;
    if (!edit) return value;
    return (
      <input className="input-field" style={{ ...box, textAlign: align, fontWeight: 400 }} value={value}
        onChange={(e) => onChange?.({ ...budget, cells: { ...(budget.cells ?? {}), [key]: e.target.value } })} />
    );
  };
  const small: React.CSSProperties = { fontSize: '10px', padding: '1px 5px' };
  const side: React.CSSProperties = { ...cell, border: 'none', padding: '1px 2px', whiteSpace: 'nowrap' };

  return (
    <tbody>
      {BUDGET_SECTIONS.map((section, sectionIndex) => (
        <Fragment key={section.key}>
          <tr>
            <td style={{ ...cell, fontWeight: 700, fontSize: '12px' }} colSpan={2}>{section.title}</td>
            {/* 엑셀은 「신청」 글씨가 첫 갈래(C7)에만 있고, 2 · 3 갈래는 같은 하늘색 칸이 비어 있다. */}
            <td style={{ ...cell, background: BLUE_BG, fontWeight: 700, textAlign: 'center' }}>
              {/* 「신청」 은 첫 갈래의 기본 글씨일 뿐 — 지우거나 고쳐 쓴다(사용자 지시 2026-11-03 「신청 안 하는 칸이 있을 수도」). */}
              {free(budgetCellKey(section.key, 'head', 'amount'), 'center', sectionIndex === 0 ? '신청' : '')}
            </td>
            <td style={cell}>{free(budgetCellKey(section.key, 'head', 'content'))}</td>
            <td style={{ ...cell, textAlign: 'center' }}>{free(budgetCellKey(section.key, 'head', 'note'), 'center')}</td>
            {edit && (
              <td style={side}>
                <button type="button" className="btn-secondary" style={small} onClick={() => appendTo(section.key)}>+ 줄</button>
              </td>
            )}
          </tr>
          {budget.lines.map((row, index) => {
            if (row.section !== section.key) return null;
            const amountKey = local ? 'local' : 'usd';
            return (
              <Fragment key={index}>
                <tr>
                  {/* 구분 — 갈래 이름(「[직영성]」)이면 엑셀처럼 진회색. 적는 표에서는 모든 칸이 입력이다(2026-11-03). */}
                  <td style={{ ...cell, textAlign: 'center', fontWeight: 700, ...(isGroupLabel(row.group) && !edit ? { background: LABEL_BG } : {}) }}>
                    {edit ? (
                      <input className="input-field" style={{ ...box, textAlign: 'center', fontWeight: 700, padding: '2px 0', fontSize: '10.5px' }} value={row.group}
                        onChange={(e) => patchLine(index, { group: e.target.value })} />
                    ) : row.group}
                  </td>
                  <td style={cell}>
                    {edit ? (
                      <input className="input-field" style={box} value={row.workName} onChange={(e) => patchLine(index, { workName: e.target.value })} />
                    ) : row.workName}
                  </td>
                  <td style={num}>
                    {/* 고른 통화만 적고 그 통화만 보인다 — 칸 밑 USD 환산은 걷었다(사용자 지시 2026-11-03). 원화 정리는 왼쪽 칸. */}
                    {edit ? (
                      <input className="input-field" style={{ ...box, textAlign: 'right' }} inputMode="decimal"
                        placeholder={local ? budget.currency : 'USD'}
                        value={groupDigits(row[amountKey])} onChange={(e) => patchLine(index, { [amountKey]: ungroupDigits(e.target.value) })} />
                    ) : formatMoney(budget.currency, lineEntered(budget, row))}
                  </td>
                  <td style={{ ...cell, whiteSpace: 'pre-wrap' }}>
                    {edit ? (
                      <input className="input-field" style={box} value={row.content} onChange={(e) => patchLine(index, { content: e.target.value })} />
                    ) : row.content}
                  </td>
                  <td style={{ ...cell, textAlign: 'center' }}>
                    {edit ? (
                      <input className="input-field" style={{ ...box, textAlign: 'center' }} value={row.note} onChange={(e) => patchLine(index, { note: e.target.value })} />
                    ) : row.note}
                  </td>
                  {edit && (
                    <td style={side}>
                      <button type="button" className="btn-secondary" style={small} onClick={() => insertAfter(index, section.key)}>+</button>{' '}
                      <button type="button" className="btn-secondary" style={small} onClick={() => removeLine(index)}>삭제</button>
                    </td>
                  )}
                </tr>
              </Fragment>
            );
          })}
          <SumRow label={section.subtotal} value={formatMoney(budget.currency, entered.sections[section.key])} bg={HEAD_BG} edit={edit}
            content={free(budgetCellKey(section.key, 'sub', 'content'))} note={free(budgetCellKey(section.key, 'sub', 'note'), 'center')} />
        </Fragment>
      ))}
      <SumRow label="합 계" value={formatMoney(budget.currency, entered.total)} bg={TOTAL_BG} edit={edit}
        content={free(budgetCellKey('TOTAL', 'sub', 'content'))} note={free(budgetCellKey('TOTAL', 'sub', 'note'), 'center')} />
    </tbody>
  );
}

function Sheet({ budget, onChange }: { budget: OverseasBudget; onChange?: (next: OverseasBudget) => void }) {
  const edit = !!onChange;
  return (
    <div>
      <div style={{ textAlign: 'right', fontSize: '11.5px', fontWeight: 700, margin: '0 0 3px', paddingRight: edit ? '7%' : 0 }}>[단위: {budget.currency || 'USD'}]</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', background: 'white' }}>
        <colgroup>
          {columns(edit).map((w, i) => <col key={i} style={{ width: `${w}%` }} />)}
        </colgroup>
        <SheetHead budget={budget} edit={edit} />
        <SheetBody budget={budget} onChange={onChange} />
      </table>
    </div>
  );
}

/** 보는 표 — 결재함 · 미리보기. */
export function OverseasBudgetSheet({ budget }: { budget: OverseasBudget }) {
  return <Sheet budget={budget} />;
}

/** 적는 표 — 공종명 · 금액 · 내용 · 비고는 종이 위 표에서 바로 적는다. 금액은 종이 밖에서 고른 통화로 적는다. */
export function OverseasBudgetInput({ budget, onChange }: { budget: OverseasBudget; onChange: (next: OverseasBudget) => void }) {
  return <Sheet budget={budget} onChange={onChange} />;
}

/**
 * 종이 밖 고르는 칸 — 현장 · 기간 · 통화 (사용자 지시 2026-11-03).
 *
 * <p>웹에서는 종이 <b>왼쪽</b>에 세로로 선다({@code vertical}) — 위에 두면 넓은 화면에서도 종이를 밀어 내려 끌어야 한다.
 *
 * <p>현장은 실제 현장 목록에서 고른다(결재 조직도의 현장). 목록에 없는 옛 값이 들어 있으면 그 값도 고르개에 남긴다.
 *
 * <p><b>환율은 적지 않는다.</b> 통화를 고르면 서버 환율 표의 그날 값(USD 는 수출입은행, KES · LAK · UZS 는 보조 통로)이
 * 들어가고, 여기서는 적용된 값을 읽기만 한다. 이미 저장된 문서의 환율은 덮지 않는다 — 상신한 날의 환율이 그 문서의 것이다.
 */
export function OverseasBudgetControls({ budget, onChange, sites, vertical = false }: {
  budget: OverseasBudget;
  onChange: (next: OverseasBudget) => void;
  sites: string[];
  vertical?: boolean;
}) {
  const local = usesLocal(budget);
  const market = useMarket();
  const rates = market.data?.rates;

  // 비어 있을 때만 채운다(새 기안 · 통화를 막 바꾼 직후 시세가 늦게 온 경우).
  useEffect(() => {
    if (!rates) return;
    if (budget.krwPerUsd.trim() !== '' && (!local || budget.localPerUsd.trim() !== '')) return;
    const next = ratesFromMarket(rates, budget.currency);
    if (next) onChange({ ...budget, ...next });
  }, [budget, rates, local, onChange]);

  const patch = (part: Partial<OverseasBudget>) => onChange({ ...budget, ...part });
  const pickCurrency = (currency: string) => {
    const next = rates ? ratesFromMarket(rates, currency) : null;
    onChange({ ...switchCurrency(budget, currency), ...(next ?? { localPerUsd: '' }) });
  };
  const options = budget.siteName && !sites.includes(budget.siteName) ? [budget.siteName, ...sites] : sites;
  // 기간은 월 · 주차 두 고르개다. 목록 밖의 옛 월이 든 문서는 그 월을 목록에 함께 남긴다.
  const period = splitPeriod(budget.period);
  const baseMonths = periodMonthOptions();
  const monthOptions = period.month && !baseMonths.includes(period.month) ? [period.month, ...baseMonths] : baseMonths;
  const field: React.CSSProperties = { fontSize: '13px', padding: '6px 10px', width: '100%' };
  const label: React.CSSProperties = vertical
    ? { display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)' }
    : { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)' };
  const rateLine = (text: string) => <div style={{ fontSize: '12px', color: 'var(--text-primary)' }}>{text}</div>;

  return (
    <div
      className="card p-3"
      style={vertical
        ? { display: 'flex', flexDirection: 'column', gap: '12px' }
        : { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 16px' }}
    >
      <label style={label}>
        현장
        <select className="input-field" style={vertical ? field : { ...field, width: 'auto', minWidth: '180px' }} value={budget.siteName}
          onChange={(e) => patch({ siteName: e.target.value })}>
          <option value="">현장을 고르세요</option>
          {options.map((name) => <option key={name} value={name}>{name}</option>)}
        </select>
      </label>
      <label style={label}>
        기간
        <div style={{ display: 'flex', gap: '6px', width: vertical ? '100%' : 'auto' }}>
          <select className="input-field" style={{ ...field, flex: 3, width: 'auto' }} value={period.month}
            onChange={(e) => patch({ period: [e.target.value, period.week].filter(Boolean).join(' ') })}>
            <option value="">월</option>
            {monthOptions.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select className="input-field" style={{ ...field, flex: 2, width: 'auto' }} value={period.week}
            onChange={(e) => patch({ period: [period.month, e.target.value].filter(Boolean).join(' ') })}>
            <option value="">월 전체</option>
            {PERIOD_WEEKS.map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
        </div>
      </label>
      <label style={label}>
        통화
        <select className="input-field" style={vertical ? field : { ...field, width: 'auto', minWidth: '170px' }} value={budget.currency}
          onChange={(e) => pickCurrency(e.target.value)}>
          {BUDGET_CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
          {!BUDGET_CURRENCIES.some((c) => c.code === budget.currency) && <option value={budget.currency}>{budget.currency}</option>}
        </select>
      </label>
      <BudgetKrwSummary budget={budget} />
      <div style={{ ...label, fontWeight: 700 }}>
        적용 환율
        <div style={{ display: 'flex', flexDirection: vertical ? 'column' : 'row', gap: vertical ? '2px' : '12px', fontWeight: 400 }}>
          {rateLine(`1 USD = ${groupDigits(budget.krwPerUsd) || '-'}원`)}
          {local && budget.currency !== 'KRW' && rateLine(`1 USD = ${budget.localPerUsd ? Number(budget.localPerUsd).toLocaleString('en-US', { maximumFractionDigits: 4 }) : '-'} ${budget.currency}`)}
        </div>
      </div>
    </div>
  );
}

/**
 * 원화 환산 정리 — <b>화면에만</b> 있는 칸(사용자 지시 2026-11-03 「실제로 얼마인지 원화로, 출력에는 안 보이게」).
 * 기안 화면의 왼쪽 칸과 결재함 창의 옆 칸이 같이 쓴다. 종이 밖이라 인쇄 · 앱 문서에 실리지 않는다.
 * 줄마다 USD 로 바꿔 원에서 한 번 접은 값의 합이고, 결재 금액이 이 합계다.
 */
export function BudgetKrwSummary({ budget, withRates = false }: { budget: OverseasBudget; withRates?: boolean }) {
  const krw = budgetTotals(budget);
  const line: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', gap: '8px', fontSize: '12px', color: 'var(--text-primary)' };
  const head: React.CSSProperties = { fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' };
  const local = usesLocal(budget) && budget.currency !== 'KRW';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div>
        <div style={head}>원화 환산</div>
        {BUDGET_SECTIONS.map((section) => (
          <div key={section.key} style={line}>
            <span>{section.title.replace(/^\d+\.\s*/, '')}</span>
            <span>{krw.sections[section.key].krw === null ? '-' : `${formatKrw(krw.sections[section.key].krw)}원`}</span>
          </div>
        ))}
        <div style={{ ...line, fontSize: '13px', fontWeight: 800, borderTop: '1px solid var(--border)', paddingTop: '3px', marginTop: '3px' }}>
          <span>합계</span>
          <span>{krw.total.krw === null ? '-' : `${formatKrw(krw.total.krw)}원`}</span>
        </div>
      </div>
      {withRates && (
        <div>
          <div style={head}>적용 환율</div>
          <div style={line}>1 USD = {groupDigits(budget.krwPerUsd) || '-'}원</div>
          {local && (
            <div style={line}>
              1 USD = {budget.localPerUsd ? Number(budget.localPerUsd).toLocaleString('en-US', { maximumFractionDigits: 4 }) : '-'} {budget.currency}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
